import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Heart, Eye, Star } from "lucide-react";
import { useShop } from "../context/ShopContext";
import { inr } from "../lib/api";
import { QuickView } from "./QuickView";

export const ProductCard = ({ product }) => {
  const { addToCart, toggleWishlist, isWishlisted, setCartOpen } = useShop();
  const [quickView, setQuickView] = useState(false);
  const discount = product.original_price
    ? Math.round(((product.original_price - product.price) / product.original_price) * 100)
    : 0;

  const handleSpotlight = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
    e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
  };

  return (
    <div
      onMouseMove={handleSpotlight}
      className="group relative bg-white ring-1 ring-gold/20 hover:ring-gold/50 hover:shadow-xl hover:shadow-maroon/10 hover:-translate-y-1 transition-all duration-300 overflow-hidden"
      data-testid={`product-card-${product.id}`}
    >
      {/* spotlight that follows the cursor */}
      <div
        className="pointer-events-none absolute inset-0 z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
        style={{ background: "radial-gradient(220px circle at var(--mx, 50%) var(--my, 50%), rgba(191,161,95,0.16), transparent 70%)" }}
      />
      <div className="relative overflow-hidden bg-surface aspect-[3/4]">
        <Link to={`/product/${product.id}`} data-testid={`product-link-${product.id}`}>
          <img
            src={product.images[0]}
            alt={`${product.name} — ${product.fabric} Ilkal saree`}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
          />
        </Link>

        <div className="absolute top-3 left-3 flex flex-col gap-2">
          {!product.in_stock && (
            <span className="bg-charcoal text-ivory text-[10px] tracking-[0.15em] uppercase px-2.5 py-1" data-testid={`badge-outofstock-${product.id}`}>Out of Stock</span>
          )}
          {product.is_new && (
            <span className="bg-gold text-charcoal text-[10px] tracking-[0.15em] uppercase px-2.5 py-1" data-testid={`badge-new-${product.id}`}>New</span>
          )}
          {discount > 0 && (
            <span className="bg-maroon text-ivory text-[10px] tracking-[0.15em] uppercase px-2.5 py-1" data-testid={`badge-discount-${product.id}`}>-{discount}%</span>
          )}
        </div>

        <button
          onClick={() => toggleWishlist(product)}
          data-testid={`wishlist-btn-${product.id}`}
          aria-label="Wishlist"
          className="absolute top-3 right-3 w-9 h-9 bg-ivory/95 shadow-sm rounded-full flex items-center justify-center transition-colors hover:bg-ivory"
        >
          <Heart
            className={`w-4 h-4 transition-colors ${isWishlisted(product.id) ? "fill-maroon text-maroon" : "text-charcoal/60"}`}
            strokeWidth={1.5}
          />
        </button>

        <div className="absolute inset-x-0 bottom-0 flex translate-y-full group-hover:translate-y-0 transition-transform duration-300">
          <button
            onClick={() => setQuickView(true)}
            data-testid={`quickview-btn-${product.id}`}
            className="flex-1 bg-ivory/95 backdrop-blur text-charcoal text-[11px] tracking-[0.15em] uppercase py-3.5 flex items-center justify-center gap-2 hover:bg-ivory"
          >
            <Eye className="w-4 h-4" strokeWidth={1.5} /> Quick View
          </button>
          <button
            onClick={() => { addToCart(product, 1, true); setCartOpen(true); }}
            disabled={!product.in_stock}
            data-testid={`add-to-cart-btn-${product.id}`}
            className="flex-1 bg-maroon text-ivory text-[11px] tracking-[0.15em] uppercase py-3.5 hover:bg-maroon-dark transition-colors disabled:bg-charcoal/60 disabled:cursor-not-allowed"
          >
            {product.in_stock ? "Add to Cart" : "Out of Stock"}
          </button>
        </div>
      </div>

      <div className="p-3.5 sm:p-4">
        <p className="text-[10px] tracking-[0.14em] uppercase text-gold-dark font-semibold">Ilkal · {product.fabric}</p>
        <Link to={`/product/${product.id}`} className="block font-heading text-base sm:text-lg text-charcoal hover:text-maroon transition-colors leading-snug mt-1">
          {product.name}
        </Link>
        <p className="text-xs text-charcoal/45 mt-0.5">Ilkal · Karnataka</p>
        <div className="flex items-center justify-between gap-2 mt-2.5">
          <div className="flex items-baseline gap-2">
            <span className="text-maroon font-semibold text-sm sm:text-base" data-testid={`price-${product.id}`}>{inr(product.price)}</span>
            {product.original_price && (
              <span className="text-xs text-charcoal/40 line-through">{inr(product.original_price)}</span>
            )}
          </div>
          <div className="flex items-center gap-1" aria-label={`${product.rating} out of 5`}>
            {Array.from({ length: 5 }).map((_, i) => (
              <Star key={i} className={`w-3 h-3 ${i < Math.round(product.rating || 0) ? "fill-gold text-gold" : "text-gold/30"}`} />
            ))}
            <span className="text-[10px] text-charcoal/40">({product.reviews_count || 0})</span>
          </div>
        </div>
      </div>

      <QuickView product={product} open={quickView} onOpenChange={setQuickView} />
    </div>
  );
};
